import { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { toast } from 'sonner';
import { db } from '@/services/firebaseService';
import { getDiffForOffer } from '@/services/offerService';
import { generateOfferDiff } from '@/utils/offerDiffUtils';
import { OfferFormValues } from '../../schemas/offerFormSchema';
import { Offer } from '@/types/offer';
import { prepareSubmissionValues } from '../utils/submissionUtils';

interface UseDiffDialogProps {
  form: UseFormReturn<OfferFormValues>;
  initialOffer?: Offer | null;
  locationId: string;
  currentUserId: string;
  isEqualToProgramValue: (fieldName: string, value: any) => boolean;
}

export const useDiffDialog = ({
  form,
  initialOffer,
  locationId,
  currentUserId,
  isEqualToProgramValue
}: UseDiffDialogProps) => {
  const [isDiffDialogOpen, setIsDiffDialogOpen] = useState(false);
  const [diffItems, setDiffItems] = useState<any[]>([]);
  const [diffData, setDiffData] = useState<{ current: any, updated: any } | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleShowDiff = async () => {
    try {
      setIsLoading(true);
      
      if (!currentUserId) {
        console.error("No user found, cannot generate diff");
        return;
      }
      
      const formValues = form.getValues();
      
      // Use prepareSubmissionValues to process the form values
      const valuesForDiff = prepareSubmissionValues(formValues, initialOffer, isEqualToProgramValue);
      
      // Get the diff data
      const result = await getDiffForOffer(
        db, 
        initialOffer?.id || null, 
        valuesForDiff, // Use the processed values
        locationId,
        currentUserId
      );
      
      setDiffData(result);
      
      // Generate the diff items
      const diffItems = generateOfferDiff(result.current, result.updated);
      setDiffItems(diffItems);
      
      // Open the diff dialog
      setIsDiffDialogOpen(true);
    } catch (error) {
      console.error("Error generating diff:", error);
      toast.error("Failed to generate diff");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmDiff = () => {
    setIsDiffDialogOpen(false);
    form.handleSubmit(() => {})(); // This will be handled by the parent
  };

  return {
    isDiffDialogOpen,
    setIsDiffDialogOpen,
    diffItems,
    diffData,
    isLoading,
    handleShowDiff,
    handleConfirmDiff
  };
};
